import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { LoginTwoFactor } from "@/components/TwoFactor";

const BG = "/lottie-login-bg.jpg";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null); // {two_factor, challenge_token}

  const finishAuth = (data) => {
    login(data.user, data.access_token);
    toast.success("Welcome back");
    nav("/app");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.two_factor) { setChallenge(data); return; }
      finishAuth(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-navy relative overflow-hidden" role="complementary" aria-label="Product highlights">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" style={{ objectPosition: "center 22%" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div>
            <h1 className="font-heading font-black text-5xl leading-[1.05] tracking-tighter">
              Ledger Optimisation Through Task Intelligence &amp; Efficiency
            </h1>
            <p className="text-white/70 mt-5 max-w-md text-base">
              Prepayments, Accruals &amp; Deferred Income on autopilot.
            </p>
          </div>
          <div className="flex items-end justify-between gap-4">
            <div className="overline" style={{ color: "rgba(242,111,33,0.85)" }}>Internal practice tool</div>
            <div className="bg-white rounded-2xl p-4 shadow-xl w-fit">
              <img src="/lottie-logo.png" alt="Lottie — your intelligent month-end assistant"
                data-testid="login-lottie-logo" className="h-28 w-auto object-contain" />
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 flex items-center justify-center px-6 bg-appbg" role="main">
        <div className="w-full max-w-md fade-up">
          <img src="/lottie-logo.png" alt="Lottie" className="lg:hidden h-24 w-auto object-contain mb-6" />
          {challenge ? (
            <LoginTwoFactor challenge={challenge} onAuthed={finishAuth} />
          ) : (
          <>
          <div className="overline mb-2" style={{ color: "#F37A24" }}>Sign in</div>
          <h2 className="font-heading font-bold text-3xl tracking-tighter text-navy mb-8">
            Access your workspace
          </h2>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Email">
              <input data-testid="login-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} className="acc-input" placeholder="you@practice.co" />
            </Field>
            <Field label="Password">
              <div className="relative">
                <input data-testid="login-password-input" type={show ? "text" : "password"} required
                  value={password} onChange={(e) => setPassword(e.target.value)} className="acc-input pr-10"
                  placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/70 hover:text-navy">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </Field>

            {error && <div data-testid="auth-error" className="text-negative text-sm font-medium">{error}</div>}

            <button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="lottie-btn w-full text-white font-semibold py-3 rounded-xl transition-colors duration-200 disabled:opacity-60">
              {loading ? "PLEASE WAIT…" : "Sign in"}
            </button>
          </form>

          <div className="mt-4 text-sm">
            <a href="/set-password" data-testid="login-forgot-link" className="font-semibold text-[#C2410C] hover:underline">
              Forgot your password?
            </a>
          </div>

          <div className="mt-3 text-sm text-navy/70">
            New here? <a href="/start" className="font-semibold text-navy hover:underline" data-testid="login-start-trial-link">Start your free trial</a>
          </div>
          </>
          )}
        </div>
      </main>

      <style>{`.acc-input{width:100%;background:#fff;border:1px solid #e2e8f0;border-radius:10px;padding:.7rem .85rem;font-size:.9rem;color:#0E0F3F;outline:none;transition:all .15s}.acc-input:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(242,111,33,.25)}.lottie-btn{background:#F37A24}.lottie-btn:hover{background:#0E0F3F}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-navy/70 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
